import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';

import { usePagination } from 'common/hooks/usePagination';
import { PageDrawerFooter } from 'common/components/PageDrawer/PageDrawerFooter';
import { FilterBar } from 'common/components/FilterBar';
import { Loader } from 'common/components/Loader';
import { ISkillListItemFragment } from 'graphql/types';
import { Button } from 'common/components/Button';
import { IPageDrawerProps } from 'common/components/PageDrawer/PageDrawer';
import { PageDrawer } from 'common/components/PageDrawer';
import { Pagination } from 'common/components/Pagination';
import { useSkillSearch } from 'skill/hooks/useSkillSearch';
import { useSkillQueryVariables } from 'skill/hooks/useSkillQueryVariables';
import { useSkillDrawerList } from 'skill/hooks/useSkillDrawerList';

import { SkillAddList } from './SkillAddList';

interface IProps extends IPageDrawerProps {
    addedSkills: ISkillListItemFragment[];
    selectedSkills: ISkillListItemFragment[];
    onAddSkills(skills?: ISkillListItemFragment[]): void;
    onClose?(): void;
    onRemoveSkill?(selectedSkill: ISkillListItemFragment): void;
    onSelectSkill?(skill: ISkillListItemFragment): void;
    setSelectedSkills?(skills: ISkillListItemFragment[]): void;
}

export const SkillSelectDrawer = ({
    addedSkills,
    selectedSkills,
    open,
    onAddSkills,
    onClose,
    onRemoveSkill,
    onSelectSkill,
    setSelectedSkills,
}: IProps) => {
    const [translate] = useTranslation();

    const { searchQueryParam, handleSearch: onSearch } = useSkillSearch();
    const { paginationSettings, initializePagination, setPage } =
        usePagination(true);

    const paginationVariables = useMemo(
        () => ({
            offset: paginationSettings.offset,
            first: paginationSettings.first,
        }),
        [paginationSettings]
    );

    const { skillQueryVariables } = useSkillQueryVariables({
        paginationVariables,
        searchQueryParam,
    });

    const {
        skills,
        skillsCount,
        loading: skillsLoading,
    } = useSkillDrawerList(skillQueryVariables);

    const resetSettings = () => {
        handleSearch('');

        setPage(1);
    };

    useEffect(() => {
        if (skillsLoading || !initializePagination) return;

        initializePagination(skillsCount || 0);
    }, [skillsCount, skillsLoading, initializePagination]);

    const handleSearch = (searchValue: string) => {
        setPage(1);
        onSearch?.(searchValue);
    };

    return (
        <PageDrawer
            open={open}
            title={translate('skillSelector.title')}
            onClose={() => {
                onClose?.();

                // When selection is changed but drawer is closed, it needs to reset the selected skills
                // else they are added, but not selected anymore
                setSelectedSkills?.(addedSkills);

                resetSettings();
            }}
        >
            <Box sx={{ pb: 22.5, pt: { xs: 2, sm: 4 }, px: { xs: 2, sm: 4 } }}>
                <FilterBar
                    initialSearchValue={searchQueryParam}
                    placeholder={translate('filterBarPlaceholder')}
                    onSearch={handleSearch}
                    onSearchClear={() => handleSearch('')}
                />

                {skillsLoading && <Loader />}

                {open && !skillsLoading && (
                    <SkillAddList
                        addedSkills={addedSkills}
                        selectedSkills={selectedSkills}
                        skills={skills}
                        onAddSkill={onSelectSkill}
                        onRemoveSkill={onRemoveSkill}
                    />
                )}
            </Box>
            <PageDrawerFooter>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {!skillsLoading && paginationSettings.count > -1 && (
                        <Box sx={{ mb: 2 }}>
                            <Pagination
                                page={paginationSettings.page}
                                pageAmount={paginationSettings.pageAmount}
                                totalsAmount={paginationSettings.count}
                                totalsText={translate('skill', {
                                    count:
                                        paginationSettings.count >= 0
                                            ? paginationSettings.count
                                            : 0,
                                })}
                                onChange={(page: number) => {
                                    setPage(page);
                                }}
                            />
                        </Box>
                    )}

                    <Button
                        color="primary"
                        disabled={!selectedSkills.length}
                        variant="contained"
                        onClick={() => {
                            onAddSkills(selectedSkills);
                            resetSettings();
                        }}
                    >
                        {!selectedSkills.length
                            ? translate('skillSelector.title')
                            : translate('skillSelector.addButtonText', {
                                  count: selectedSkills.length,
                              })}
                    </Button>
                </Box>
            </PageDrawerFooter>
        </PageDrawer>
    );
};
